import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { students, assessmentTemplates, assessmentSessions } from '../api/client';

const GRADE_ORDER = ['Pre-K', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const ALL_GRADES = ['All Grades', ...GRADE_ORDER];

const CATEGORY_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'academic', label: 'Academic' },
  { value: 'prek_readiness', label: 'Pre-K Readiness' },
  { value: 'course', label: 'Course-Specific' },
  { value: 'life_skills', label: 'Life Skills' },
  { value: 'screening', label: 'Learning Difference Screening' },
];

function getGradeIndex(grade) {
  const idx = GRADE_ORDER.indexOf(grade);
  return idx === -1 ? 999 : idx;
}

function getTemplateGradeIndex(template) {
  // Use grade_min if available, otherwise try to extract from name
  if (template.grade_min) {
    return getGradeIndex(template.grade_min);
  }
  // Fallback: try to extract grade from template name
  const name = template.name.toLowerCase();
  if (name.includes('pre-k') || name.includes('prek')) return getGradeIndex('Pre-K');
  if (name.includes('kindergarten') || name.match(/\bk\b/)) return getGradeIndex('K');
  const gradeMatch = name.match(/(\d+)(st|nd|rd|th)?\s*grade/i) || name.match(/grade\s*(\d+)/i);
  if (gradeMatch) {
    return getGradeIndex(gradeMatch[1]);
  }
  return 999;
}

export default function AssessmentsPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [starting, setStarting] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [error, setError] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState('All Grades');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadData();
  }, [studentId]);

  const loadData = async () => {
    try {
      const [studentRes, templatesRes, sessionsRes] = await Promise.all([
        students.get(studentId),
        assessmentTemplates.list(studentId),
        assessmentSessions.listForStudent(studentId),
      ]);
      setStudent(studentRes.data);
      setTemplates(templatesRes.data);
      setSessions(sessionsRes.data);
      // Default grade filter to student's grade level
      if (studentRes.data.grade_level) {
        setSelectedGrade(studentRes.data.grade_level);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssessment = async (templateId) => {
    setStarting(templateId);
    try {
      const response = await assessmentSessions.create(studentId, templateId);
      navigate(`/app/assessments/${response.data.id}`);
    } catch (err) {
      console.error('Failed to start assessment:', err);
      setError(err.response?.data?.error || 'Failed to start assessment');
      setStarting(null);
    }
  };

  const handleContinueAssessment = (sessionId) => {
    navigate(`/app/assessments/${sessionId}`);
  };

  const handleViewResults = (sessionId) => {
    navigate(`/app/assessments/${sessionId}/results`);
  };

  const handlePrintAssessment = async (templateId, templateName) => {
    setPrinting(templateId);
    try {
      const response = await assessmentTemplates.print(templateId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${templateName.toLowerCase().replace(/\s+/g, '-')}-printable.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to print assessment:', err);
      setError('Failed to generate printable assessment. Please try again.');
    } finally {
      setPrinting(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      not_started: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      abandoned: 'bg-red-100 text-red-800',
    };
    const labels = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      completed: 'Completed',
      abandoned: 'Abandoned',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-red-500">Student not found</div>
      </div>
    );
  }

  // Separate in-progress and completed sessions
  const inProgressSessions = sessions.filter((s) => s.status === 'in_progress');
  const completedSessions = sessions.filter((s) => s.status === 'completed');

  // Filter available templates (those not already in progress)
  const inProgressTemplateIds = inProgressSessions.map((s) => s.template_id);

  // Sort templates by grade order
  const sortedTemplates = [...templates].sort((a, b) => {
    return getTemplateGradeIndex(a) - getTemplateGradeIndex(b);
  });

  // Filter by selected grade, category, and exclude in-progress
  const availableTemplates = sortedTemplates.filter((t) => {
    // Exclude templates already in progress
    if (inProgressTemplateIds.includes(t.id)) return false;

    // Filter by category
    if (selectedCategory !== 'all') {
      if (t.category !== selectedCategory) return false;
    }

    // Filter by selected grade
    if (selectedGrade !== 'All Grades') {
      // Match if selected grade falls within template's grade range
      const selectedIdx = getGradeIndex(selectedGrade);
      const minIdx = t.grade_min ? getGradeIndex(t.grade_min) : 0;
      const maxIdx = t.grade_max ? getGradeIndex(t.grade_max) : GRADE_ORDER.length - 1;

      if (selectedIdx < minIdx || selectedIdx > maxIdx) return false;
    }

    return true;
  });

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-4">
        <Link
          to={`/app/students/${studentId}`}
          className="text-sm text-indigo-600 hover:text-indigo-500"
        >
          &larr; Back to {student.full_name}
        </Link>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Assessments for {student.full_name}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {student.age ? `${student.age} years old` : ''}
          {student.grade_level ? ` | Grade ${student.grade_level}` : ''}
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* In Progress Assessments */}
      {inProgressSessions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Continue Assessment</h2>
          <div className="space-y-4">
            {inProgressSessions.map((session) => (
              <div
                key={session.id}
                className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-md font-medium text-gray-900">
                      {session.template_name}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {session.progress_percentage}% complete | Started{' '}
                      {new Date(session.started_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleContinueAssessment(session.id)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700"
                  >
                    Continue
                  </button>
                </div>
                <div className="mt-3">
                  <div className="w-full bg-yellow-200 rounded-full h-2">
                    <div
                      className="bg-yellow-600 h-2 rounded-full"
                      style={{ width: `${session.progress_percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Assessments */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2 className="text-lg font-medium text-gray-900">Available Assessments</h2>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="category-filter" className="text-sm text-gray-500">
                Type:
              </label>
              <select
                id="category-filter"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="text-sm border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                {CATEGORY_FILTERS.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <label htmlFor="grade-filter" className="text-sm text-gray-500">
                Grade:
              </label>
              <select
                id="grade-filter"
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="text-sm border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                {ALL_GRADES.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade === 'All Grades' ? grade : `Grade ${grade}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {availableTemplates.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {availableTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-md font-medium text-gray-900">{template.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                    <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
                      <span>{template.total_questions} questions</span>
                      <span>{template.estimated_minutes} min</span>
                      {template.age_min && template.age_max && (
                        <span>Ages {template.age_min}-{template.age_max}</span>
                      )}
                      {template.grade_min && (
                        <span>
                          Grade {template.grade_min}
                          {template.grade_max && template.grade_max !== template.grade_min && `-${template.grade_max}`}
                        </span>
                      )}
                    </div>
                    {template.is_recommended === false && (
                      <div className="mt-2 text-xs text-amber-600">
                        Note: This assessment is designed for a different age/grade range
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => handleStartAssessment(template.id)}
                    disabled={starting === template.id}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {starting === template.id ? 'Starting...' : 'Start Assessment'}
                  </button>
                  <button
                    onClick={() => handlePrintAssessment(template.id, template.name)}
                    disabled={printing === template.id}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    title="Print blank assessment"
                  >
                    {printing === template.id ? (
                      <svg className="animate-spin h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
            <p className="text-gray-500">
              {selectedGrade !== 'All Grades' || selectedCategory !== 'all' ? (
                <>
                  No assessments match the selected filters.{' '}
                  <button
                    onClick={() => {
                      setSelectedGrade('All Grades');
                      setSelectedCategory('all');
                    }}
                    className="text-indigo-600 hover:text-indigo-500 font-medium"
                  >
                    Clear filters
                  </button>
                </>
              ) : sessions.length > 0 ? (
                'All available assessments are either in progress or completed.'
              ) : (
                'No assessments are available for this student at this time.'
              )}
            </p>
          </div>
        )}
      </div>

      {/* Completed Assessments */}
      {completedSessions.length > 0 && (
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Completed Assessments</h2>
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <ul className="divide-y divide-gray-200">
              {completedSessions.map((session) => (
                <li key={session.id}>
                  <div className="px-4 py-4 sm:px-6 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {session.template_name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Completed {new Date(session.completed_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      {getStatusBadge(session.status)}
                      <button
                        onClick={() => handleViewResults(session.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        View Results
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
